import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { numberError } from "../answerValidation";
import type { QuestionProps } from "../types";
import { QuestionShell } from "./QuestionShell";
import { Input } from "@/components/ui/input";

export function NumberInput(props: QuestionProps) {
  const { t } = useTranslation();
  const id = useId();
  const { question, value, onChange, disabled } = props;
  const [text, setText] = useState(typeof value === "number" ? String(value) : "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Accepting a suggestion changes `value` from outside.
    if (typeof value === "number" && Number(text) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handle = (raw: string) => {
    setText(raw);
    if (raw.trim() === "") {
      setError(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setError(t("questionnaire.numberInvalid"));
      return;
    }
    const problem = numberError(n, question.min, question.max);
    if (problem) {
      setError(
        problem === "range"
          ? t("questionnaire.numberRange", { min: question.min, max: question.max })
          : problem === "min"
            ? t("questionnaire.numberMin", { min: question.min })
            : t("questionnaire.numberMax", { max: question.max }),
      );
      return;
    }
    setError(null);
    onChange(n);
  };

  return (
    <QuestionShell props={props} inputId={id} error={error}>
      {({ helpId, errorId }) => (
        <div className="flex items-center gap-3">
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            className="max-w-40"
            min={question.min}
            max={question.max}
            aria-describedby={[helpId, errorId].filter(Boolean).join(" ") || undefined}
            aria-invalid={error ? true : undefined}
            value={text}
            disabled={disabled}
            onChange={(e) => handle(e.target.value)}
          />
          {question.unit ? <span className="text-fg-muted">{question.unit}</span> : null}
        </div>
      )}
    </QuestionShell>
  );
}
